import openai from '../lib/openai';

// Add rate limiting functionality at the top of the file
const rateLimiter = {
  calls: [],
  maxCallsPerMinute: 10, // Conservative limit for quota management
  
  canMakeCall() {
    const now = Date.now();
    // Remove calls older than 1 minute
    this.calls = this.calls?.filter(timestamp => now - timestamp < 60000);
    
    return this.calls?.length < this.maxCallsPerMinute;
  },
  
  recordCall() {
    this.calls?.push(Date.now());
  },
  
  getWaitTime() {
    if (this.calls?.length === 0) return 0;
    const oldestCall = Math.min(...this.calls);
    const waitTime = 60000 - (Date.now() - oldestCall);
    return Math.max(0, waitTime);
  }
};

/**
 * Wrapper function to add rate limiting to OpenAI calls
 * @param {Function} aiFunction - The OpenAI function to call
 * @param {...any} args - Arguments to pass to the function
 * @returns {Promise} Result of the AI function or fallback
 */
async function withRateLimit(aiFunction, ...args) {
  if (!rateLimiter?.canMakeCall()) {
    const waitTime = rateLimiter?.getWaitTime();
    console.warn(`Rate limit reached. Next call available in ${Math.ceil(waitTime / 1000)} seconds.`);
    throw new Error(`Rate limit exceeded. Please wait ${Math.ceil(waitTime / 1000)} seconds before trying again.`);
  }
  
  rateLimiter?.recordCall();
  return await aiFunction(...args);
}

/**
 * Parses a .pages file content using OpenAI to extract song analysis data.
 * Includes comprehensive error handling for API quota and rate limits.
 * @param {string} fileContent - The text content extracted from the .pages file.
 * @returns {Promise<Object>} Structured song data ready for database insertion.
 */
export async function parseSongFromPagesFile(fileContent) {
  try {
    if (!fileContent || fileContent?.trim()?.length < 10) {
      console.warn('File content is too short, using fallback parsing');
      return fallbackParseContent(fileContent, 'Content too short for AI analysis');
    }

    // Check API key availability first
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
      console.warn('OpenAI API key not configured, using fallback parsing');
      return fallbackParseContent(fileContent, 'OpenAI API key not configured');
    }

    // Use rate-limited OpenAI call
    const response = await withRateLimit(async () => {
      return await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini', // Using cost-effective model
        max_tokens: 1500, // Further reduced to save quota
        temperature: 0.1, // Lower temperature for more consistent results
        messages: [
          {
            role: 'system',
            content: `You are an expert musical theater song analysis assistant. Parse the provided file content and extract song information into a structured JSON format.

Extract when available: title, musical, act, scene, tempo, key, time signature, duration, lyrics, character info, analysis sections.
If information is missing, use empty strings or appropriate defaults.
Always return valid JSON only.`
          },
          {
            role: 'user',
            content: `Parse this content and return JSON:\n\n${fileContent?.substring(0, 3000)}` // Further reduced content size
          }
        ]
      });
    });

    let parsedData;
    try {
      const content = response?.choices?.[0]?.message?.content;
      if (!content) {
        console.warn('No content received from OpenAI, using fallback');
        return fallbackParseContent(fileContent, 'No response content from OpenAI');
      }
      
      // Try to parse JSON
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        parsedData = JSON.parse(jsonMatch?.[0]);
      } else {
        parsedData = JSON.parse(content);
      }
    } catch (parseError) {
      console.warn('Failed to parse OpenAI response as JSON, using fallback parsing:', parseError);
      return fallbackParseContent(fileContent, 'Failed to parse AI response');
    }
    
    // Apply defaults and clean up data
    const result = {
      title: parsedData?.title || extractTitleFromContent(fileContent) || 'Untitled Song',
      musicalTitle: parsedData?.musicalTitle || parsedData?.musical || '',
      actNumber: parsedData?.actNumber || parsedData?.act || 1,
      sceneNumber: parsedData?.sceneNumber || parsedData?.scene || null,
      songOrder: parsedData?.songOrder || null,
      tempo: parsedData?.tempo || null,
      keySignature: parsedData?.keySignature || parsedData?.key || null,
      timeSignature: parsedData?.timeSignature || '4/4',
      durationSeconds: parsedData?.durationSeconds || parsedData?.duration || null,
      lyrics: parsedData?.lyrics || '',
      overview: parsedData?.overview || {},
      level1: parsedData?.level1 || '',
      level2: parsedData?.level2 || '',
      level3: Array.isArray(parsedData?.level3) ? parsedData?.level3 : [],
      level4: parsedData?.level4 || {},
      level5: parsedData?.level5 || {},
      phrases: Array.isArray(parsedData?.phrases) ? parsedData?.phrases : [],
      confidence: parsedData?.confidence || 0.8,
      parsedWithAI: true,
      aiParsingSuccess: true,
      quotaUsed: true // Track quota usage
    };
    
    return result;
  } catch (error) {
    console.error('Error parsing .pages file with OpenAI:', error);
    
    // Enhanced error analysis
    const errorAnalysis = analyzeOpenAIError(error);
    console.warn(errorAnalysis?.message);
    
    // ALWAYS return fallback data instead of throwing error
    const fallbackResult = fallbackParseContent(fileContent, errorAnalysis?.userMessage || 'AI parsing failed');
    
    // Add error context to result but don't throw
    fallbackResult.errorContext = {
      type: errorAnalysis?.type || 'unknown_error',
      originalError: errorAnalysis?.message,
      userMessage: errorAnalysis?.userMessage || 'AI parsing unavailable. Using basic parsing instead.',
      isQuotaIssue: errorAnalysis?.type === 'quota_exceeded' || errorAnalysis?.type === 'rate_limited'
    };
    
    return fallbackResult;
  }
}

/**
 * Parses a PDF file content using OpenAI to extract song analysis data.
 * Includes comprehensive error handling for API quota and rate limits.
 * @param {string} fileContent - The text content extracted from the PDF file.
 * @returns {Promise<Object>} Structured song data ready for database insertion.
 */
export async function parseSongFromPDFFile(fileContent) {
  try {
    if (!fileContent || fileContent?.trim()?.length < 10) {
      console.warn('File content is too short, using fallback parsing');
      return fallbackParseContent(fileContent, 'Content too short for AI analysis');
    }

    // Check API key availability first
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
      console.warn('OpenAI API key not configured, using fallback parsing');
      return fallbackParseContent(fileContent, 'OpenAI API key not configured');
    }

    // Use rate-limited OpenAI call
    const response = await withRateLimit(async () => {
      return await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini', // Using cost-effective model for PDF parsing
        max_tokens: 1500, // Reduced for PDF content
        temperature: 0.1, // Lower temperature for more consistent results
        messages: [
          {
            role: 'system',
            content: `You are an expert musical theater song analysis assistant. Parse the provided PDF content and extract song information into a structured JSON format.

Extract when available: title, musical, act, scene, tempo, key, time signature, duration, lyrics, character info, analysis sections.
If information is missing, use empty strings or appropriate defaults.
Always return valid JSON only.

Focus on identifying:
- Song titles (often in headings or emphasized text)
- Musical/show names
- Character information
- Lyrics or song text
- Technical details (tempo, key, etc.)
- Any analysis sections or notes`
          },
          {
            role: 'user',
            content: `Parse this PDF content and return JSON:\n\n${fileContent?.substring(0, 3000)}` // Reduced content size for PDF
          }
        ]
      });
    });

    let parsedData;
    try {
      const content = response?.choices?.[0]?.message?.content;
      if (!content) {
        console.warn('No content received from OpenAI, using fallback');
        return fallbackParseContent(fileContent, 'No response content from OpenAI');
      }
      
      // Try to parse JSON
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        parsedData = JSON.parse(jsonMatch?.[0]);
      } else {
        parsedData = JSON.parse(content);
      }
    } catch (parseError) {
      console.warn('Failed to parse OpenAI response as JSON, using fallback parsing:', parseError);
      return fallbackParseContent(fileContent, 'Failed to parse AI response');
    }
    
    // Apply defaults and clean up data
    const result = {
      title: parsedData?.title || extractTitleFromContent(fileContent) || 'Untitled Song',
      musicalTitle: parsedData?.musicalTitle || parsedData?.musical || '',
      actNumber: parsedData?.actNumber || parsedData?.act || 1,
      sceneNumber: parsedData?.sceneNumber || parsedData?.scene || null,
      songOrder: parsedData?.songOrder || null,
      tempo: parsedData?.tempo || null,
      keySignature: parsedData?.keySignature || parsedData?.key || null,
      timeSignature: parsedData?.timeSignature || '4/4',
      durationSeconds: parsedData?.durationSeconds || parsedData?.duration || null,
      lyrics: parsedData?.lyrics || '',
      overview: parsedData?.overview || {},
      level1: parsedData?.level1 || '',
      level2: parsedData?.level2 || '',
      level3: Array.isArray(parsedData?.level3) ? parsedData?.level3 : [],
      level4: parsedData?.level4 || {},
      level5: parsedData?.level5 || {},
      phrases: Array.isArray(parsedData?.phrases) ? parsedData?.phrases : [],
      confidence: parsedData?.confidence || 0.8,
      parsedWithAI: true,
      aiParsingSuccess: true,
      quotaUsed: true // Track quota usage
    };
    
    return result;
  } catch (error) {
    console.error('Error parsing PDF file with OpenAI:', error);
    
    // Enhanced error analysis
    const errorAnalysis = analyzeOpenAIError(error);
    console.warn(errorAnalysis?.message);
    
    // ALWAYS return fallback data instead of throwing error
    const fallbackResult = fallbackParseContent(fileContent, errorAnalysis?.userMessage || 'AI parsing failed');
    
    // Add error context to result but don't throw
    fallbackResult.errorContext = {
      type: errorAnalysis?.type || 'unknown_error',
      originalError: errorAnalysis?.message,
      userMessage: errorAnalysis?.userMessage || 'AI parsing unavailable. Using basic parsing instead.',
      isQuotaIssue: errorAnalysis?.type === 'quota_exceeded' || errorAnalysis?.type === 'rate_limited'
    };
    
    return fallbackResult;
  }
}

/**
 * Parses a text file content using OpenAI to extract song analysis data.
 * Includes comprehensive error handling for API quota and rate limits.
 * @param {string} fileContent - The text content from the .txt file.
 * @returns {Promise<Object>} Structured song data ready for database insertion.
 */
export async function parseSongFromTextFile(fileContent) {
  try {
    if (!fileContent || fileContent?.trim()?.length < 10) {
      console.warn('File content is too short, using fallback parsing');
      return fallbackParseContent(fileContent, 'Content too short for AI analysis');
    }

    // Check API key availability first
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
      console.warn('OpenAI API key not configured, using fallback parsing');
      return fallbackParseContent(fileContent, 'OpenAI API key not configured');
    }

    // Use rate-limited OpenAI call
    const response = await withRateLimit(async () => {
      return await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini', // Using cost-effective model for text parsing
        max_tokens: 1500, // Reduced for text content
        temperature: 0.1, // Lower temperature for more consistent results
        messages: [
          {
            role: 'system',
            content: `You are an expert musical theater song analysis assistant. Parse the provided text content and extract song information into a structured JSON format.

Extract when available: title, musical, act, scene, tempo, key, time signature, duration, lyrics, character info, analysis sections.
If information is missing, use empty strings or appropriate defaults.
Always return valid JSON only.

Focus on identifying:
- Song titles (often in headings or at the beginning)
- Musical/show names
- Character information
- Lyrics or song text
- Technical details (tempo, key, etc.)
- Any analysis sections or notes
- Structure markers (verses, chorus, bridge)`
          },
          {
            role: 'user',
            content: `Parse this text file content and return JSON:\n\n${fileContent?.substring(0, 3000)}` // Reduced content size for text
          }
        ]
      });
    });

    let parsedData;
    try {
      const content = response?.choices?.[0]?.message?.content;
      if (!content) {
        console.warn('No content received from OpenAI, using fallback');
        return fallbackParseContent(fileContent, 'No response content from OpenAI');
      }
      
      // Try to parse JSON
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        parsedData = JSON.parse(jsonMatch?.[0]);
      } else {
        parsedData = JSON.parse(content);
      }
    } catch (parseError) {
      console.warn('Failed to parse OpenAI response as JSON, using fallback parsing:', parseError);
      return fallbackParseContent(fileContent, 'Failed to parse AI response');
    }
    
    // Apply defaults and clean up data
    const result = {
      title: parsedData?.title || extractTitleFromContent(fileContent) || 'Untitled Song',
      musicalTitle: parsedData?.musicalTitle || parsedData?.musical || '',
      actNumber: parsedData?.actNumber || parsedData?.act || 1,
      sceneNumber: parsedData?.sceneNumber || parsedData?.scene || null,
      songOrder: parsedData?.songOrder || null,
      tempo: parsedData?.tempo || null,
      keySignature: parsedData?.keySignature || parsedData?.key || null,
      timeSignature: parsedData?.timeSignature || '4/4',
      durationSeconds: parsedData?.durationSeconds || parsedData?.duration || null,
      lyrics: parsedData?.lyrics || '',
      overview: parsedData?.overview || {},
      level1: parsedData?.level1 || '',
      level2: parsedData?.level2 || '',
      level3: Array.isArray(parsedData?.level3) ? parsedData?.level3 : [],
      level4: parsedData?.level4 || {},
      level5: parsedData?.level5 || {},
      phrases: Array.isArray(parsedData?.phrases) ? parsedData?.phrases : [],
      confidence: parsedData?.confidence || 0.8,
      parsedWithAI: true,
      aiParsingSuccess: true,
      quotaUsed: true // Track quota usage
    };
    
    return result;
  } catch (error) {
    console.error('Error parsing text file with OpenAI:', error);
    
    // Enhanced error analysis
    const errorAnalysis = analyzeOpenAIError(error);
    console.warn(errorAnalysis?.message);
    
    // ALWAYS return fallback data instead of throwing error
    const fallbackResult = fallbackParseContent(fileContent, errorAnalysis?.userMessage || 'AI parsing failed');
    
    // Add error context to result but don't throw
    fallbackResult.errorContext = {
      type: errorAnalysis?.type || 'unknown_error',
      originalError: errorAnalysis?.message,
      userMessage: errorAnalysis?.userMessage || 'AI parsing unavailable. Using basic parsing instead.',
      isQuotaIssue: errorAnalysis?.type === 'quota_exceeded' || errorAnalysis?.type === 'rate_limited'
    };
    
    return fallbackResult;
  }
}

/**
 * Extracts text content from a PDF file using PDF.js.
 * Enhanced with better error handling and validation.
 * @param {File} file - The PDF file to extract text from.
 * @returns {Promise<string>} Extracted text content.
 */
export async function extractTextFromPDFFile(file) {
  try {
    if (!file) {
      throw new Error('No file provided');
    }

    if (file?.size === 0) {
      throw new Error('File is empty');
    }

    if (file?.size > 10 * 1024 * 1024) { // 10MB limit
      throw new Error('File is too large (maximum 10MB)');
    }

    // Validate file type
    if (file?.type !== 'application/pdf' && !file?.name?.toLowerCase()?.endsWith('.pdf')) {
      throw new Error('Invalid file type. Please select a PDF file.');
    }

    // Dynamic import of PDF.js to avoid bundling issues
    const pdfjs = await import('pdfjs-dist');
    
    // Set up PDF.js worker
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs?.version}/pdf.worker.min.js`;

    // Convert file to array buffer
    const arrayBuffer = await file?.arrayBuffer();
    
    // Load PDF document
    const pdf = await pdfjs?.getDocument({ data: arrayBuffer })?.promise;
    
    let fullText = '';
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf?.numPages; pageNum++) {
      try {
        const page = await pdf?.getPage(pageNum);
        const textContent = await page?.getTextContent();
        
        // Combine all text items
        const pageText = textContent?.items?.map(item => item?.str)?.join(' ');
        fullText += pageText + '\n';
      } catch (pageError) {
        console.warn(`Error extracting text from page ${pageNum}:`, pageError);
        // Continue with other pages
      }
    }
    
    // Clean up the extracted text
    const cleanText = fullText
      ?.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, '') // Remove control characters
      ?.replace(/\s+/g, ' ') // Normalize whitespace
      ?.trim();
    
    if (!cleanText || cleanText?.length < 10) {
      throw new Error('PDF file appears to be empty or contains only images/graphics. Please ensure the PDF contains readable text.');
    }
    
    return cleanText;
  } catch (error) {
    console.error('Error extracting text from PDF file:', error);
    
    // Enhanced error messages for specific cases
    if (error?.message?.includes('Invalid PDF')) {
      throw new Error('Invalid or corrupted PDF file. Please try a different file.');
    } else if (error?.message?.includes('password')) {
      throw new Error('Password-protected PDFs are not supported. Please provide an unprotected PDF file.');
    } else {
      throw new Error(`Failed to extract text from PDF file: ${error?.message}`);
    }
  }
}

/**
 * Fallback parsing function when OpenAI is unavailable or fails.
 * Enhanced with better text extraction and user-friendly messaging.
 * @param {string} fileContent - The file content to parse.
 * @param {string} reason - Reason for fallback parsing.
 * @returns {Object} Basic song data structure.
 */
function fallbackParseContent(fileContent, reason = 'AI parsing unavailable') {
  if (!fileContent || fileContent?.trim()?.length < 10) {
    return createEmptyParsedData('Empty File', reason);
  }

  try {
    const lines = fileContent?.split('\n')?.filter(line => line?.trim());
    let title = extractTitleFromContent(fileContent);
    let musical = extractMusicalFromContent(fileContent);
    let lyrics = extractLyricsFromContent(fileContent);

    // Enhanced title extraction - if no title found, use first meaningful line
    if (!title || title?.length < 3) {
      title = findBestTitleCandidate(lines);
    }

    // If still no title found, create a timestamp-based title
    if (!title || title?.length < 3) {
      const timestamp = new Date()?.toLocaleDateString();
      title = `Imported Song - ${timestamp}`;
    }

    return {
      title: title || 'Imported Song',
      musicalTitle: musical || '',
      actNumber: 1,
      sceneNumber: null,
      songOrder: null,
      tempo: null,
      keySignature: null,
      timeSignature: '4/4',
      durationSeconds: null,
      lyrics: lyrics || '',
      overview: {
        musical: musical || '',
        songTitle: title || '',
        character: '',
        context: 'Parsed from file using fallback method - manual review recommended',
        purpose: `Auto-imported content - ${reason?.toLowerCase()}. AI parsing was unavailable, so basic parsing was used instead.`,
        importNotes: 'This import used fallback parsing due to AI service limitations. Please review and enhance the data as needed.'
      },
      level1: `Content imported from uploaded file. ${reason}. Please review and edit as needed. AI parsing was not available, so basic text extraction was used.`,
      level2: '',
      level3: [],
      level4: {},
      level5: {},
      phrases: [],
      confidence: 0.5,
      parsedWithAI: false,
      fallbackParsed: true,
      requiresReview: true,
      fallbackReason: reason,
      quotaUsed: false // No quota used for fallback
    };
  } catch (error) {
    console.error('Fallback parsing also failed:', error);
    return createEmptyParsedData('Parse Error', reason);
  }
}

/**
 * Enhanced title extraction with multiple strategies.
 * @param {Array} lines - Array of text lines from the file.
 * @returns {string|null} Best title candidate or null.
 */
function findBestTitleCandidate(lines) {
  if (!lines || lines?.length === 0) return null;
  
  // Strategy 1: Look for lines that look like titles (all caps, title case, quoted)
  for (const line of lines?.slice(0, 10)) { // Check first 10 lines only
    const trimmed = line?.trim();
    if (trimmed?.length > 3 && trimmed?.length < 80) {
      // Check if it's all uppercase (likely a title)
      if (trimmed === trimmed?.toUpperCase() && /^[A-Z\s&'-]+$/?.test(trimmed)) {
        return trimmed;
      }
      // Check if it's title case
      if (/^[A-Z][a-z]+(\s+[A-Z][a-z]*)*$/?.test(trimmed)) {
        return trimmed;
      }
      // Check if it's in quotes
      if ((trimmed?.startsWith('"') && trimmed?.endsWith('"')) || 
          (trimmed?.startsWith("'") && trimmed?.endsWith("'"))) {
        return trimmed?.slice(1, -1);
      }
    }
  }
  
  // Strategy 2: Use first non-empty line if reasonable length
  const firstLine = lines?.[0]?.trim();
  if (firstLine && firstLine?.length > 3 && firstLine?.length < 80) {
    return firstLine;
  }
  
  return null;
}

/**
 * Creates an empty parsed data structure with minimal defaults.
 * @param {string} title - Default title to use.
 * @param {string} reason - Reason for empty data creation.
 * @returns {Object} Empty song data structure.
 */
function createEmptyParsedData(title = 'Untitled Song', reason = 'No content available') {
  return {
    title,
    musicalTitle: '',
    actNumber: 1,
    sceneNumber: null,
    songOrder: null,
    tempo: null,
    keySignature: null,
    timeSignature: '4/4',
    durationSeconds: null,
    lyrics: '',
    overview: {
      songTitle: title,
      context: `File imported with basic parsing - ${reason}`,
      purpose: 'Manual review and editing required'
    },
    level1: `File imported using basic parsing. ${reason}. Please add content manually.`,
    level2: '',
    level3: [],
    level4: {},
    level5: {},
    phrases: [],
    confidence: 0.3,
    parsedWithAI: false,
    fallbackParsed: true,
    isEmpty: true,
    fallbackReason: reason
  };
}

/**
 * Extracts potential title from file content using common patterns.
 * @param {string} content - File content.
 * @returns {string|null} Extracted title or null.
 */
function extractTitleFromContent(content) {
  if (!content) return null;
  
  const titlePatterns = [
    /(?:Title|Song|Name):\s*(.+)/i,
    /(?:^|\n)([A-Z][A-Z\s&'-]+)(?:\n|$)/,
    /(?:^|\n)"([^"]+)"/,
    /(?:^|\n)'([^'])'/
  ];

  for (const pattern of titlePatterns) {
    const match = content?.match(pattern);
    if (match && match?.[1] && match?.[1]?.trim()?.length > 2 && match?.[1]?.trim()?.length < 100) {
      return match?.[1]?.trim();
    }
  }

  // Try first non-empty line as title
  const lines = content?.split('\n')?.filter(line => line?.trim());
  if (lines?.length > 0 && lines?.[0]?.length < 100) {
    return lines?.[0]?.trim();
  }

  return null;
}

/**
 * Extracts musical/show name from file content.
 * @param {string} content - File content.
 * @returns {string|null} Extracted musical name or null.
 */
function extractMusicalFromContent(content) {
  if (!content) return null;
  
  const musicalPatterns = [
    /(?:Musical|Show|From):\s*(.+)/i,
    /(?:^|\n)(?:from\s+)?(.+?)\s*(?:musical|show)/i
  ];

  for (const pattern of musicalPatterns) {
    const match = content?.match(pattern);
    if (match && match?.[1] && match?.[1]?.trim()?.length > 2 && match?.[1]?.trim()?.length < 100) {
      return match?.[1]?.trim();
    }
  }

  return null;
}

/**
 * Extracts lyrics or main text content from file.
 * @param {string} content - File content.
 * @returns {string} Extracted lyrics or content.
 */
function extractLyricsFromContent(content) {
  if (!content) return '';
  
  // Remove common file artifacts and clean up
  let cleanContent = content?.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, '')?.replace(/\s+/g, ' ')?.trim();

  // If content is too long, take a reasonable portion
  if (cleanContent?.length > 2000) {
    cleanContent = cleanContent?.substring(0, 2000) + '...';
  }

  return cleanContent;
}

/**
 * Enhanced duplicate detection with OpenAI fallback.
 * Uses simple string comparison when OpenAI is unavailable.
 * @param {Object} newSongData - The new song data to check.
 * @param {Array} existingSongs - Array of existing songs to compare against.
 * @returns {Promise<Object>} Duplicate detection results.
 */
export async function detectSongDuplicatesWithAI(newSongData, existingSongs) {
  try {
    if (!existingSongs || existingSongs?.length === 0) {
      return { hasDuplicates: false, duplicates: [], confidence: 1.0 };
    }

    // Filter potential matches by title similarity first
    const potentialMatches = existingSongs?.filter(song => {
      const titleSimilarity = calculateStringSimilarity(
        newSongData?.title?.toLowerCase() || '',
        song?.title?.toLowerCase() || ''
      );
      const musicalSimilarity = calculateStringSimilarity(
        newSongData?.musicalTitle?.toLowerCase() || '',
        song?.musical?.title?.toLowerCase() || ''
      );
      
      return titleSimilarity > 0.6 || musicalSimilarity > 0.8;
    });

    if (potentialMatches?.length === 0) {
      return { hasDuplicates: false, duplicates: [], confidence: 1.0 };
    }

    // Try OpenAI for semantic duplicate detection with rate limiting
    try {
      const response = await withRateLimit(async () => {
        return await openai?.chat?.completions?.create({
          model: 'gpt-4o-mini',
          max_tokens: 1000, // Reduced for duplicate detection
          temperature: 0.1,
          messages: [
            {
              role: 'system',
              content: 'You are a musical theater expert. Analyze if songs are duplicates based on title, musical, and context. Consider variations in spelling, abbreviations, and alternate titles. Return valid JSON only.'
            },
            {
              role: 'user',
              content: `Analyze if this new song is a duplicate of any existing songs. Return JSON format:
{
  "hasDuplicates": boolean,
  "duplicates": [{"songId": "string", "title": "string", "musical": "string", "similarity": number, "reason": "string"}],
  "confidence": number
}

NEW SONG:
Title: "${newSongData?.title}"
Musical: "${newSongData?.musicalTitle}"

EXISTING SONGS:
${potentialMatches?.slice(0, 3)?.map((song, idx) => `
${idx + 1}. ID: "${song?.id}", Title: "${song?.title}", Musical: "${song?.musical?.title || ''}"
`)?.join('\n')}`
            }
          ]
        });
      });

      let aiResult;
      try {
        const content = response?.choices?.[0]?.message?.content;
        const jsonMatch = content?.match(/\{[\s\S]*\}/);
        aiResult = JSON.parse(jsonMatch ? jsonMatch?.[0] : content);
      } catch (parseError) {
        console.warn('Failed to parse AI duplicate detection response, using fallback');
        return fallbackDuplicateDetection(newSongData, existingSongs);
      }
      
      // Match AI results back to actual song objects
      const duplicateSongs = aiResult?.duplicates?.map(duplicate => {
        const matchedSong = potentialMatches?.find(song => 
          song?.id === duplicate?.songId ||
          song?.title === duplicate?.title
        );
        return {
          ...duplicate,
          song: matchedSong
        };
      })?.filter(item => item?.song);

      return {
        hasDuplicates: aiResult?.hasDuplicates && duplicateSongs?.length > 0,
        duplicates: duplicateSongs,
        confidence: aiResult?.confidence || 0.8,
        originalData: newSongData,
        detectedWithAI: true,
        quotaUsed: true
      };
    } catch (aiError) {
      console.warn('AI duplicate detection failed, using fallback:', aiError);
      return fallbackDuplicateDetection(newSongData, existingSongs);
    }
  } catch (error) {
    console.error('Error in duplicate detection:', error);
    return fallbackDuplicateDetection(newSongData, existingSongs);
  }
}

/**
 * Fallback duplicate detection using simple string comparison.
 * @param {Object} newSongData - The new song data to check.
 * @param {Array} existingSongs - Array of existing songs to compare against.
 * @returns {Object} Duplicate detection results.
 */
function fallbackDuplicateDetection(newSongData, existingSongs) {
  try {
    const duplicates = existingSongs?.filter(song => {
      const titleSimilarity = calculateStringSimilarity(
        newSongData?.title?.toLowerCase() || '',
        song?.title?.toLowerCase() || ''
      );
      const musicalSimilarity = calculateStringSimilarity(
        newSongData?.musicalTitle?.toLowerCase() || '',
        song?.musical?.title?.toLowerCase() || ''
      );
      
      return titleSimilarity > 0.85 && musicalSimilarity > 0.85;
    });

    return {
      hasDuplicates: duplicates?.length > 0,
      duplicates: duplicates?.map(song => ({
        songId: song?.id,
        title: song?.title,
        musical: song?.musical?.title || '',
        similarity: 0.9,
        reason: 'High string similarity match (fallback detection)',
        song
      })),
      confidence: 0.7,
      originalData: newSongData,
      detectedWithAI: false
    };
  } catch (error) {
    console.error('Fallback duplicate detection failed:', error);
    return {
      hasDuplicates: false,
      duplicates: [],
      confidence: 0.5,
      originalData: newSongData,
      detectedWithAI: false,
      error: error?.message
    };
  }
}

/**
 * Calculate string similarity using simple character matching.
 * @param {string} str1 - First string.
 * @param {string} str2 - Second string.
 * @returns {number} Similarity score between 0 and 1.
 */
function calculateStringSimilarity(str1, str2) {
  if (!str1 || !str2) return 0;
  if (str1 === str2) return 1;
  
  const longer = str1?.length > str2?.length ? str1 : str2;
  const shorter = str1?.length > str2?.length ? str2 : str1;
  
  if (longer?.length === 0) return 1;
  
  const editDistance = levenshteinDistance(longer, shorter);
  return (longer?.length - editDistance) / longer?.length;
}

/**
 * Calculate Levenshtein distance between two strings.
 * @param {string} str1 - First string.
 * @param {string} str2 - Second string.
 * @returns {number} Edit distance.
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];
  
  for (let i = 0; i <= str2?.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1?.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2?.length; i++) {
    for (let j = 1; j <= str1?.length; j++) {
      if (str2?.charAt(i - 1) === str1?.charAt(j - 1)) {
        matrix[i][j] = matrix?.[i - 1]?.[j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix?.[i - 1]?.[j - 1] + 1,
          matrix?.[i]?.[j - 1] + 1,
          matrix?.[i - 1]?.[j] + 1
        );
      }
    }
  }
  
  return matrix?.[str2?.length]?.[str1?.length];
}

/**
 * Enhanced API error detection and handling.
 * @param {Error} error - The error object to analyze.
 * @returns {Object} Error analysis with type and recommended action.
 */
function analyzeOpenAIError(error) {
  const errorMessage = error?.message || '';
  const statusCode = error?.status || error?.response?.status;
  
  // Quota exceeded (429)
  if (statusCode === 429 || error?.error?.code === 'insufficient_quota' || errorMessage?.includes('exceeded your current quota')) {
    return {
      type: 'quota_exceeded',
      message: 'OpenAI quota exceeded. Using fallback parsing method.',
      userMessage: 'AI parsing temporarily unavailable due to quota limits. Files will be imported using basic parsing instead.',
      shouldFallback: true,
      severity: 'warning'
    };
  }
  
  // Rate limit exceeded
  if (error?.error?.code === 'rate_limit_exceeded' || errorMessage?.includes('rate limit')) {
    return {
      type: 'rate_limited',
      message: 'OpenAI rate limit exceeded. Using fallback parsing method.',
      userMessage: 'AI service temporarily busy. Please wait a moment before trying again, or continue with basic parsing.',
      shouldFallback: true,
      severity: 'warning'
    };
  }
  
  // Invalid API key
  if (statusCode === 401 || error?.error?.code === 'invalid_api_key') {
    return {
      type: 'auth_error',
      message: 'OpenAI API key invalid. Using fallback parsing method.',
      userMessage: 'AI parsing unavailable (configuration issue). Files will be imported using basic parsing instead.',
      shouldFallback: true,
      severity: 'error'
    };
  }
  
  // Network or other errors
  return {
    type: 'unknown_error',
    message: `OpenAI error: ${errorMessage}`,
    userMessage: 'AI parsing temporarily unavailable. Files will be imported using basic parsing instead.',
    shouldFallback: true,
    severity: 'error'
  };
}

/**
 * Enhanced OpenAI availability checker with better error handling.
 * @returns {Promise<Object>} Availability status with details.
 */
export async function checkOpenAIAvailability() {
  try {
    if (!import.meta.env?.VITE_OPENAI_API_KEY || 
        import.meta.env?.VITE_OPENAI_API_KEY === 'your-openai-api-key-here') {
      return {
        available: false,
        reason: 'no_api_key',
        message: 'OpenAI API key not configured'
      };
    }

    // Check rate limiter first
    if (!rateLimiter?.canMakeCall()) {
      const waitTime = rateLimiter?.getWaitTime();
      return {
        available: false,
        reason: 'rate_limited',
        message: `Rate limit reached. Next call available in ${Math.ceil(waitTime / 1000)} seconds.`,
        waitTime: waitTime
      };
    }

    // Simple test call with minimal tokens
    const response = await withRateLimit(async () => {
      return await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        max_tokens: 5,
        messages: [{ role: 'user', content: 'test' }]
      });
    });

    return {
      available: response?.choices?.[0]?.message?.content ? true : false,
      reason: 'success',
      message: 'OpenAI API available and operational'
    };
  } catch (error) {
    const errorAnalysis = analyzeOpenAIError(error);
    return {
      available: false,
      reason: errorAnalysis?.type,
      message: errorAnalysis?.message,
      originalError: error?.message,
      severity: errorAnalysis?.severity
    };
  }
}

/**
 * Generates a video transcript and structured captions using OpenAI Whisper.
 * Step 1: Transcribe audio with verbose_json to get word-level timestamps.
 * Step 2: Use gpt-4o-mini to extract key teaching points per segment.
 * @param {Blob|File} audioBlob - Audio blob or file extracted from the video
 * @param {string} videoTitle - Title of the video for context
 * @returns {Promise<{transcript: string, captions: Array}>}
 */
export async function generateVideoTranscript(audioBlob, videoTitle = '') {
  const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
  if (!apiKey || apiKey === 'your-openai-api-key-here' || apiKey?.trim() === '') {
    throw new Error('OpenAI API key not configured');
  }

  // Step 1: Transcribe with Whisper using verbose_json for timestamps
  // Accept File or Blob — ensure a proper filename with extension for Whisper format detection
  const fileName = audioBlob instanceof File ? audioBlob?.name : 'audio.mp4';
  const mimeType = audioBlob?.type || 'video/mp4';
  const fileToSend = audioBlob instanceof File ? audioBlob : new File([audioBlob], fileName, { type: mimeType });

  // Use the OpenAI SDK client instead of raw fetch for reliability
  const whisperData = await openai?.audio?.transcriptions?.create({
    file: fileToSend,
    model: 'whisper-1',
    response_format: 'verbose_json',
    timestamp_granularities: ['segment'],
  });

  const fullTranscript = whisperData?.text || '';
  const segments = whisperData?.segments || [];

  if (!segments?.length) {
    return { transcript: fullTranscript, captions: [] };
  }

  // Step 2: Use gpt-4o-mini to extract teaching points per segment
  const segmentSummary = segments
    ?.slice(0, 40) // Limit to 40 segments to avoid token overflow
    ?.map((seg, i) => `[${i}] ${Math.round(seg?.start || 0)}s: "${seg?.text?.trim()}"`)
    ?.join('\n');

  const teachingResponse = await openai?.chat?.completions?.create({
    model: 'gpt-4o-mini',
    max_tokens: 2000,
    temperature: 0.2,
    messages: [
      {
        role: 'system',
        content: `You are a musical theater education expert. Analyze video transcript segments and identify key teaching points.
For each segment, determine:
1. A concise teaching_point (what concept is being taught, max 10 words, or empty string if not a teaching moment)
2. Whether it is a key_point (true if it's a critical concept students must remember)

Return ONLY a JSON array with one object per segment index provided, in this exact format:
[{"index": 0, "teaching_point": "...", "is_key_point": false}, ...]`
      },
      {
        role: 'user',
        content: `Video: "${videoTitle}"\n\nSegments:\n${segmentSummary}\n\nReturn the JSON array for all ${Math.min(segments?.length, 40)} segments.`
      }
    ]
  });

  let teachingPoints = [];
  try {
    const content = teachingResponse?.choices?.[0]?.message?.content || '[]';
    const jsonMatch = content?.match(/\[[\s\S]*\]/);
    teachingPoints = jsonMatch ? JSON.parse(jsonMatch?.[0]) : [];
  } catch (parseErr) {
    console.warn('Failed to parse teaching points, using empty defaults:', parseErr);
    teachingPoints = [];
  }

  // Build structured captions array
  const captions = segments?.map((seg, i) => {
    const tp = teachingPoints?.find(t => t?.index === i);
    return {
      timestamp_seconds: Math.round(seg?.start || 0),
      end_seconds: Math.round(seg?.end || 0),
      text: seg?.text?.trim() || '',
      teaching_point: tp?.teaching_point || '',
      is_key_point: tp?.is_key_point || false,
    };
  });

  return { transcript: fullTranscript, captions };
}

// Update the legacy function names to maintain compatibility
const parseSongFromPagesFileAlias = parseSongFromPagesFile;
const extractTextFromPagesFile = extractTextFromPDFFile;

export { parseSongFromPagesFileAlias };
export { extractTextFromPagesFile };

export default {
  parseSongFromPagesFile,
  parseSongFromPDFFile,
  parseSongFromTextFile,
  extractTextFromPDFFile,
  extractTextFromPagesFile: extractTextFromPDFFile,
  detectSongDuplicatesWithAI,
  checkOpenAIAvailability,
  rateLimiter // Export for debugging purposes
};